import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Truck, Gift, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Seo } from "../components/Seo";
import { AffiliateEarnings } from "../components/AffiliateEarnings";
import { AccountPassword } from "../components/AccountPassword";

export default function MyOrders() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState(null);
  const [refStats, setRefStats] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/my")
      .then(({ data }) => setOrders(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/settings")
      .then(({ data }) => setRewards({ enabled: !!data.rewards_enabled, redeemRate: data.rewards_redeem_rate || 100 }))
      .catch(() => {});
    api.get("/referrals/my").then(({ data }) => setRefStats(data)).catch(() => {});
  }, [user]);

  if (authLoading || (user && loading)) {
    return (
      <div className="relative z-10 flex justify-center py-40">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="relative z-10 max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]">Sign in to view your orders</h1>
        <button data-testid="orders-signin-button" onClick={() => navigate("/login?next=/my-orders")} className="mt-8 bg-[#8C7B6B] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-semibold px-10 py-4 rounded-none hover:bg-[#75665A] transition-colors">
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="My Orders" path="/my-orders" noindex />
      <h1 className="font-display text-lg uppercase tracking-wide sm:text-3xl uppercase tracking-wide text-[#1A1A1A] mb-12">My Orders</h1>

      {rewards?.enabled && (user.points || 0) > 0 && (
        <div className="flex items-center gap-3 bg-[#F6F1E7] border border-[#D4B36A]/50 rounded-lg px-5 py-4 mb-10" data-testid="rewards-balance-banner">
          <Gift className="w-5 h-5 text-[#8C7B6B] shrink-0" />
          <p className="text-sm text-[#1A1A1A]">
            You have <strong data-testid="rewards-points-balance">{user.points}</strong> reward points — worth{" "}
            <strong>${(user.points / rewards.redeemRate).toFixed(2)}</strong> off your next order. Apply them at checkout.
          </p>
        </div>
      )}

      {rewards?.enabled && (
        <div className="border border-[#DDD7CF] rounded-lg bg-white px-5 py-4 mb-10" data-testid="referral-card">
          <p className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#8C7B6B]" /> Refer a friend — you both get 100 points
          </p>
          <p className="text-xs text-[#6E675F] mt-1.5">
            Share your link. When your friend creates an account and their first order is paid, you each receive 100 reward points.
          </p>
          {refStats && (refStats.completed > 0 || refStats.pending > 0) && (
            <div className="flex items-center gap-4 mt-3 flex-wrap text-xs" data-testid="referral-stats">
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#1A1A1A]" data-testid="referral-stats-completed">
                <span className="text-[#2D6A4F] text-sm font-bold">{refStats.completed}</span>
                friend{refStats.completed === 1 ? "" : "s"} referred
              </span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#8A6D2F]" data-testid="referral-stats-points">
                <Gift className="w-3.5 h-3.5" /> {refStats.points_earned.toLocaleString()} points earned
              </span>
              {refStats.pending > 0 && (
                <span className="text-[#6E675F]" data-testid="referral-stats-pending">
                  {refStats.pending} signed up — awaiting first order
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <code className="text-xs bg-[#EFEBE6] rounded-md px-3 py-2 break-all" data-testid="referral-link">
              {`${window.location.origin}/login?ref=${encodeURIComponent(user.email)}`}
            </code>
            <button
              data-testid="referral-copy-button"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/login?ref=${encodeURIComponent(user.email)}`);
                toast.success("Referral link copied");
              }}
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.1em] uppercase font-semibold text-white bg-[#8C7B6B] hover:bg-[#75665A] rounded-none px-4 py-2 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>
        </div>
      )}

      <AffiliateEarnings />

      <AccountPassword />

      {orders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-12 h-12 text-[#8C7B6B] mx-auto mb-5" />
          <p className="text-[#6E675F]" data-testid="no-orders-message">You haven't placed any orders yet.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="my-orders-list">
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/order/${o.id}`}
              data-testid={`order-row-${o.order_number}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#DDD7CF] rounded-lg p-6 bg-white hover:border-[#8C7B6B] transition-colors"
            >
              <div>
                <p className="font-medium text-[#1A1A1A]">{o.order_number}</p>
                <p className="text-sm text-[#6E675F] mt-1">
                  {new Date(o.created_at).toLocaleDateString()} · {o.items.reduce((s, i) => s + i.quantity, 0)} item(s)
                </p>
                {o.tracking_number && (
                  <p className="text-xs text-[#8C7B6B] font-semibold mt-1.5 flex items-center gap-1.5" data-testid={`order-tracking-${o.order_number}`}>
                    <Truck className="w-3.5 h-3.5" /> Tracking: {o.tracking_number}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-5">
                <StatusBadge status={o.status} />
                <p className="text-lg font-medium text-[#1A1A1A]">${o.total.toFixed(2)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
